import { useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import YupPassword from 'yup-password'
YupPassword(yup)

import {
  REGISTER_FINANCES_USER_KEY,
  registerFinancesUser,
} from '@/actions/authActions'
import { getFetchError } from '@/reducers/fetch'
import { useSignupLastPageNavigate } from '@/services/onboardingService'

import {
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { STATES } from '@/constants/locationConstants'

import { useAnalyticsSetUserId, useAnalyticsView } from '@/features/amplitude'

import {
  getFieldName,
  makeReqStringSchema,
  makeReqPhoneNumberSchema,
} from '@/base'
import { TypographyStyles } from '@/styles'
import { SIGNUP_PATHS } from '../helpers'
import { useFreeTrialEnabled } from '../useFreeTrialEnabled'
import {
  buildSignupPayload,
  sharedSignupFieldRules,
  type AboutYouFormValues,
} from '../signupForm.contract'
import { Text, Alert, Link, Form, Card, CardColors } from '@/ui'
import { FreeTrialSignupBanner } from '../FreeTrialSignupBanner'
import { WhatYouGetCard } from './WhatYouGetCard'
import { ReferralWelcomeMessage } from '@/features/user/referrals/WelcomeMessage'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'

import { useReferralFetch } from '@/features/user/referrals/helpers'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '../SignupFooter'
import { SignupFormFieldsWrapper } from '../SignupFormFieldsWrapper'
import { useScrollToFieldError } from '@/utils/formHelpers'

export const AboutYou = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigateWithPersistParams()
  const pageView = useAnalyticsView()
  const setAnalyticsUserId = useAnalyticsSetUserId()
  const { scrollRef, scrollToRef } = useScrollRef()
  const { recipientCode } = useParams()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const signupError = useReselector(getFetchError, REGISTER_FINANCES_USER_KEY)
  const [searchParams] = useSearchParams()
  const isReferral = searchParams.get('trial') === 'referral'

  const freeTrialEnabled = useFreeTrialEnabled(searchParams.get('trial'))

  const { recipient, loading: recipientLoading } =
    useReferralFetch(recipientCode)

  const validationSchema = useMemo(() => {
    return yup.object({
      ...sharedSignupFieldRules,
      phoneNumber: makeReqPhoneNumberSchema(),
      state: makeReqStringSchema({ field: 'practicing state' }),
      smsContactPhoneNumber: yup
        .string()
        .when('smsConsent', ([smsConsent], schema) =>
          smsConsent === true ? makeReqPhoneNumberSchema() : schema.nullable()
        ),
    })
  }, [])

  useSignupLastPageNavigate()

  useEffect(() => {
    if (
      signupError?.message ===
        'It looks like an account with this email exists already.' &&
      sessionStorage.getItem('signupEmail')
    ) {
      sessionStorage.removeItem('signupEmail')
    }
  }, [signupError])

  useEffect(() => {
    pageView('sign up about you')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const signupEmail = sessionStorage.getItem('signupEmail')

  const formik = useFormik<AboutYouFormValues>({
    initialValues: {
      firstName: recipient?.firstName || '',
      lastName: recipient?.lastName || '',
      email: signupEmail || recipient?.email || '',
      phoneNumber: '',
      state: '',
      password: '',
      confirmPassword: '',
      smsConsent: false,
      smsContactPhoneNumber: '',
      sameAsBusinessPhoneNumber: false,
      freeTrialPromoCode: null,
      termsOfService: false,
    },
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values) => {
      const res = await registerFinancesUser(
        buildSignupPayload({ values, searchParams })
      )(dispatch)

      if (res) {
        if (signupEmail) {
          sessionStorage.removeItem('signupEmail')
        }

        setAnalyticsUserId(res.user.uuid)
        navigate(SIGNUP_PATHS.aboutYourPractice)
      } else {
        scrollToRef()
      }
    },
  })

  useScrollToFieldError(formik)

  const onBackClick = () => {
    if (signupEmail) {
      sessionStorage.removeItem('signupEmail')
    }
    navigate(SIGNUP_PATHS.emailCapture)
  }

  const { values, submitForm, isSubmitting, setFieldValue } = formik

  return (
    <SignupPageLayout
      maxWidth="wide"
      footer={
        <SignupFooter
          onBack={onBackClick}
          onContinue={submitForm}
          continueDisabled={isSubmitting}
          continueLoading={isSubmitting}
          continueId="btn-signup-create-account"
        />
      }
    >
      {signupError && (
        <div className="w-full mb-4">
          <span ref={scrollRef} />
          <Alert variant="error" description={signupError.message} />
        </div>
      )}

      {recipientCode && (
        <ReferralWelcomeMessage
          freeTrialEnabled={freeTrialEnabled}
          recipient={recipient}
          recipientLoading={recipientLoading}
        />
      )}

      {!recipientCode && (
        <>
          <div className="w-full mb-4">
            <Text>{freeTrialEnabled && <FreeTrialSignupBanner />}</Text>
          </div>

          <div className="w-full mb-8">
            <Text variant={TypographyStyles.DISPLAY_MD}>
              {isReferral ? 'Welcome' : 'Tell us about yourself'}
            </Text>
          </div>
        </>
      )}

      <section className="flex flex-col md:flex-row justify-between gap-14">
        <SignupFormFieldsWrapper>
          <FormikProvider value={formik}>
            <div className="flex flex-col gap-8">
              <Form.Input
                label="First Name"
                name={getFieldName<typeof values>('firstName')}
              />

              <Form.Input
                label="Last Name"
                name={getFieldName<typeof values>('lastName')}
              />

              {!signupEmail && (
                <Form.Input
                  className="w-full"
                  label="Business Email Address"
                  disabled={Boolean(recipient?.email)}
                  name={getFieldName<typeof values>('email')}
                />
              )}

              <div className="flex flex-col gap-3">
                <Form.Input
                  label="Business Phone"
                  placeholder="555-555-5555"
                  componentType="phone"
                  name={getFieldName<typeof values>('phoneNumber')}
                />

                <Form.Checkbox
                  label="Get important tax and bookeeping reminders via SMS"
                  name={getFieldName<typeof values>('smsConsent')}
                />
              </div>

              {values.smsConsent && (
                <div>
                  <div className="ml-6 flex flex-col gap-3">
                    {!values.sameAsBusinessPhoneNumber && (
                      <Form.Input
                        label="What mobile phone do you want text reminders sent
                              to?"
                        placeholder="555-555-5555"
                        componentType="phone"
                        name={getFieldName<typeof values>(
                          'smsContactPhoneNumber'
                        )}
                      />
                    )}

                    <Form.Checkbox
                      className="mb-2"
                      label="Same as business phone"
                      name={getFieldName<typeof values>(
                        'sameAsBusinessPhoneNumber'
                      )}
                      onCheckedChange={(checked) => {
                        setFieldValue('sameAsBusinessPhoneNumber', checked)
                        setFieldValue(
                          'smsContactPhoneNumber',
                          checked ? values.phoneNumber : ''
                        )
                      }}
                    />

                    <div className="mb-2">
                      <Text
                        variant={TypographyStyles.BODY_XS}
                        className="text-neutral-400"
                      >
                        You agree to receive recurring text messages from Heard
                        about account updates and service reminders at the
                        mobile phone number provided. Messages frequency varies.
                        Message and date rates may apply. Reply STOP to cancel.
                        View our{' '}
                        <Link
                          className="text-xs"
                          newPage
                          href="https://www.joinheard.com/terms-of-service"
                        >
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link
                          className="text-xs"
                          newPage
                          href="https://www.joinheard.com/privacy"
                        >
                          Privacy Policy
                        </Link>
                        .
                      </Text>
                    </div>
                  </div>
                </div>
              )}

              <Form.Autocomplete
                label="Home State"
                placeholder="Select State"
                description="Your primary residence is in this state."
                name={getFieldName<typeof values>('state')}
                autoComplete="address-level1"
                options={STATES.map((state) => state.text)}
              />

              <Form.Input
                required
                label="Password"
                description="Your password needs to have more than 8 characters, and must include a number and a symbol."
                name={getFieldName<typeof values>('password')}
                type="password"
                autoComplete="new-password"
              />

              <Form.Input
                required
                label="Confirm Password"
                name={getFieldName<typeof values>('confirmPassword')}
                type="password"
                autoComplete="new-password"
              />

              <Card color={CardColors.PRIORITY} className="w-full">
                <Form.Checkbox
                  name={getFieldName<typeof values>('termsOfService')}
                  className="[&_label]:text-sm"
                  label={
                    (
                      <span>
                        By continuing, you&apos;re creating a Heard account and
                        agree to the{' '}
                        <Link
                          newPage
                          href="https://www.joinheard.com/terms-of-service"
                        >
                          Terms of Service
                        </Link>
                        ,{' '}
                        <Link newPage href="https://www.joinheard.com/privacy">
                          Privacy Policy
                        </Link>
                        , and{' '}
                        <Link
                          newPage
                          href="https://www.joinheard.com/baa-agreement"
                        >
                          Business Associate Agreement
                        </Link>
                        .
                      </span>
                    ) as unknown as string
                  }
                />
              </Card>
            </div>
          </FormikProvider>
        </SignupFormFieldsWrapper>

        {!isMobile && (
          <WhatYouGetCard className="w-[300px] shrink-0 self-start" />
        )}
      </section>

      {isMobile && <WhatYouGetCard className="mt-8" />}
    </SignupPageLayout>
  )
}
